import { useEffect, useState } from 'react'
import { Link, NavLink, useLocation } from 'react-router-dom'
import {
  Package,
  Image,
  Video,
  Sparkles,
  Layers,
  Wand2,
  Film,
  User,
  ListTodo,
  Settings,
  CreditCard,
  Receipt,
  ChevronDown,
  FolderOpen,
  Lightbulb,
  FlaskConical,
  LayoutDashboard,
  BookOpen,
  Clapperboard,
  BarChart3,
  FileText,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { useAuthStore } from '@/stores/auth'
import logoSrc from '@/assets/logo.svg'

// 光谱色彩映射 - 使用完整类名（Tailwind 不支持动态类名）
const spectrumStyles = {
  overview: {
    active: 'bg-spectrum-blue/10 text-spectrum-blue font-medium',
    icon: 'text-spectrum-blue',
    indicator: 'bg-spectrum-blue',
  },
  competitorResearch: {
    active: 'bg-spectrum-teal/10 text-spectrum-teal font-medium',
    icon: 'text-spectrum-teal',
    indicator: 'bg-spectrum-teal',
  },
  variation: {
    active: 'bg-spectrum-cyan/10 text-spectrum-cyan font-medium',
    icon: 'text-spectrum-cyan',
    indicator: 'bg-spectrum-cyan',
  },
  imageFreestyle: {
    active: 'bg-spectrum-blue/10 text-spectrum-blue font-medium',
    icon: 'text-spectrum-blue',
    indicator: 'bg-spectrum-blue',
  },
  videoFreestyle: {
    active: 'bg-spectrum-purple/10 text-spectrum-purple font-medium',
    icon: 'text-spectrum-purple',
    indicator: 'bg-spectrum-purple',
  },
  digitalHuman: {
    active: 'bg-spectrum-magenta/10 text-spectrum-magenta font-medium',
    icon: 'text-spectrum-magenta',
    indicator: 'bg-spectrum-magenta',
  },
  products: {
    active: 'bg-spectrum-coral/10 text-spectrum-coral font-medium',
    icon: 'text-spectrum-coral',
    indicator: 'bg-spectrum-coral',
  },
  library: {
    active: 'bg-spectrum-orange/10 text-spectrum-orange font-medium',
    icon: 'text-spectrum-orange',
    indicator: 'bg-spectrum-orange',
  },
  drama: {
    active: 'bg-spectrum-magenta/10 text-spectrum-magenta font-medium',
    icon: 'text-spectrum-magenta',
    indicator: 'bg-spectrum-magenta',
  },
  creativeLab: {
    active: 'bg-spectrum-emerald/10 text-spectrum-emerald font-medium',
    icon: 'text-spectrum-emerald',
    indicator: 'bg-spectrum-emerald',
  },
  ideas: {
    active: 'bg-spectrum-lime/10 text-spectrum-lime font-medium',
    icon: 'text-spectrum-lime',
    indicator: 'bg-spectrum-lime',
  },
  verticalIdeas: {
    active: 'bg-spectrum-emerald/10 text-spectrum-emerald font-medium',
    icon: 'text-spectrum-emerald',
    indicator: 'bg-spectrum-emerald',
  },
  tasks: {
    active: 'bg-spectrum-orange/10 text-spectrum-orange font-medium',
    icon: 'text-spectrum-orange',
    indicator: 'bg-spectrum-orange',
  },
  settings: {
    active: 'bg-spectrum-coral/10 text-spectrum-coral font-medium',
    icon: 'text-spectrum-coral',
    indicator: 'bg-spectrum-coral',
  },
} as const

interface CollapsibleSectionProps {
  name: string
  icon: React.ComponentType<{ className?: string }>
  children: React.ReactNode
  defaultOpen?: boolean
  /** 当前路由命中本分组时强制展开（默认折叠的分组也能露出激活高亮） */
  activeWhen?: boolean
}

function CollapsibleSection({
  name,
  icon: Icon,
  children,
  defaultOpen = true,
  activeWhen = false,
}: CollapsibleSectionProps) {
  const [isOpen, setIsOpen] = useState(defaultOpen || activeWhen)

  useEffect(() => {
    if (activeWhen) setIsOpen(true)
  }, [activeWhen])

  return (
    <div className="space-y-1">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center justify-between w-full px-3 py-2 text-sm font-medium text-gray-700 rounded-lg hover:bg-gray-100/80 transition-colors"
      >
        <div className="flex items-center gap-3">
          <Icon className="h-5 w-5" />
          {name}
        </div>
        <ChevronDown className={cn(
          "h-4 w-4 text-gray-400 transition-transform duration-200",
          !isOpen && "-rotate-90"
        )} />
      </button>
      <div className={cn(
        "ml-4 space-y-1 overflow-hidden transition-all duration-200",
        isOpen ? "max-h-96 opacity-100" : "max-h-0 opacity-0"
      )}>
        {children}
      </div>
    </div>
  )
}

function NavLinkItem({
  name,
  href,
  icon: Icon,
  spectrumKey,
  end = false,
}: {
  name: string
  href: string
  icon: React.ComponentType<{ className?: string }>
  spectrumKey?: keyof typeof spectrumStyles
  end?: boolean
}) {
  const styles = spectrumKey ? spectrumStyles[spectrumKey] : null

  return (
    <NavLink
      to={href}
      end={end}
      className={({ isActive }) =>
        cn(
          'relative flex items-center gap-3 px-3 py-2 text-sm rounded-lg transition-all duration-150',
          isActive
            ? styles?.active || 'bg-primary/10 text-primary font-medium'
            : 'text-gray-600 hover:bg-gray-100/80 hover:text-gray-900 hover:translate-x-0.5'
        )
      }
    >
      {({ isActive }) => (
        <>
          {isActive && (
            <div className={cn(
              "absolute left-0 top-1/2 -translate-y-1/2 w-[3px] h-5 rounded-full",
              styles?.indicator || 'bg-primary'
            )} />
          )}
          <Icon className={cn('h-5 w-5', isActive && styles?.icon)} />
          {name}
        </>
      )}
    </NavLink>
  )
}

export function Sidebar() {
  const { user, allowedPages } = useAuthStore()
  const { pathname } = useLocation()
  const showFinance = (allowedPages ?? []).includes('finance')
  const showMaterialAnalytics = (allowedPages ?? []).includes('material_analytics')

  return (
    <div className="w-64 bg-gradient-to-b from-slate-50 to-white border-r border-gray-200/80 flex flex-col">
      {/* Logo */}
      <div className="h-20 border-b border-gray-200/80">
        <Link
          to="/home"
          aria-label="Go to overview"
          className="h-full px-4 flex items-center gap-1 group"
        >
          <img src={logoSrc} alt="LinkToCreative" className="h-[4.5rem] object-contain" />
          <span className="text-lg font-bold font-brand tracking-tight text-gray-900 group-hover:text-gray-700 transition-colors">
            LinkToCreative
          </span>
        </Link>
      </div>

      {/* Navigation */}
      <nav className="flex-1 px-4 py-4 space-y-2 overflow-y-auto scrollbar-thin">
        {/* Overview */}
        <NavLinkItem name="Overview" href="/home" icon={LayoutDashboard} spectrumKey="overview" />

        {/* Generate Section */}
        <CollapsibleSection name="Generate" icon={Sparkles} defaultOpen={true}>
          {/* Image Generation */}
          <CollapsibleSection
            name="Image"
            icon={Image}
            defaultOpen={false}
            activeWhen={pathname.startsWith('/generate/image')}
          >
            <NavLinkItem name="Competitor Research" href="/generate/image/competitor" icon={Sparkles} spectrumKey="competitorResearch" />
            <NavLinkItem name="Variation" href="/generate/image/variation" icon={Layers} spectrumKey="variation" />
            <NavLinkItem name="Freestyle" href="/generate/image/freestyle" icon={Wand2} spectrumKey="imageFreestyle" />
          </CollapsibleSection>

          {/* Video Generation */}
          <CollapsibleSection
            name="Video"
            icon={Video}
            defaultOpen={false}
            activeWhen={pathname.startsWith('/generate/video')}
          >
            <NavLinkItem name="Digital Human" href="/generate/video/digital-human" icon={User} spectrumKey="digitalHuman" />
            <NavLinkItem name="Freestyle" href="/generate/video/freestyle" icon={Film} spectrumKey="videoFreestyle" />
          </CollapsibleSection>

          {/* Drama mix */}
          <NavLinkItem name="Drama Mix" href="/drama" icon={Clapperboard} spectrumKey="drama" />
        </CollapsibleSection>

        {/* Creative Lab */}
        <CollapsibleSection
          name="Creative Lab"
          icon={FlaskConical}
          defaultOpen={false}
          activeWhen={pathname.startsWith('/creative-lab')}
        >
          <NavLinkItem name="Ideas" href="/creative-lab/ideas" icon={Lightbulb} spectrumKey="ideas" />
          <NavLinkItem name="Vertical Ideas" href="/creative-lab/vertical-ideas" icon={BookOpen} spectrumKey="verticalIdeas" />
        </CollapsibleSection>

        {/* Analytics (email-whitelisted) */}
        {showMaterialAnalytics && (
          <CollapsibleSection
            name="Analytics"
            icon={BarChart3}
            defaultOpen={false}
            activeWhen={pathname.startsWith('/analytics')}
          >
            <NavLinkItem name="Asset Analytics" href="/analytics/materials" icon={Image} />
            <NavLinkItem name="Feedback Analysis" href="/analytics/finance-report" icon={FileText} />
          </CollapsibleSection>
        )}

        {/* More Creatives：图片变体（finance 白名单）+ 视频日审（内部） */}
        {(showFinance || user?.is_internal) && (
          <CollapsibleSection
            name="More Creatives"
            icon={Layers}
            defaultOpen={false}
            activeWhen={
              pathname.startsWith('/finance/feedback') || pathname.startsWith('/internal/daily-review')
            }
          >
            {showFinance && (
              <NavLinkItem name="Image variants" href="/finance/feedback" icon={Image} />
            )}
            {user?.is_internal && (
              <NavLinkItem name="Video Daily Review" href="/internal/daily-review" icon={Video} />
            )}
          </CollapsibleSection>
        )}

        {/* Products */}
        <NavLinkItem name="Products" href="/products" icon={Package} spectrumKey="products" />

        {/* Library */}
        <NavLinkItem name="Library" href="/library" icon={FolderOpen} spectrumKey="library" />

        {/* Tasks */}
        <NavLinkItem name="Tasks" href="/tasks" icon={ListTodo} spectrumKey="tasks" />

        {/* Settings Section */}
        <CollapsibleSection
          name="Settings"
          icon={Settings}
          defaultOpen={false}
          activeWhen={pathname.startsWith('/settings')}
        >
          <NavLinkItem name="Profile" href="/settings" icon={User} spectrumKey="settings" end />
          <NavLinkItem name="Subscription" href="/settings/subscription" icon={CreditCard} spectrumKey="settings" />
          <NavLinkItem name="Billing History" href="/settings/billing" icon={Receipt} spectrumKey="settings" />
        </CollapsibleSection>
      </nav>
    </div>
  )
}
